import React from 'react'
import { Company } from 'models'
import type { SelectionState } from 'contexts/CompaniesContext'
import { entityDisplayName } from 'utils/helpers'
import { EMPTY_SELECTION_SUMMARY, SelectionSummary } from '../../types/selectionTypes'

const useChildSelectionView = (params: {
  pendingSelectionState: SelectionState
  pendingSelectionLimit: number
  pendingSelectionLimitReached: boolean
  isChildSelectorDisabled: boolean
  allAccountsOption?: Company
  isOrphanSelected: boolean
  childrenSearchValue: string
}) => {
  const {
    pendingSelectionState,
    pendingSelectionLimit,
    pendingSelectionLimitReached,
    isChildSelectorDisabled,
    allAccountsOption,
    isOrphanSelected,
    childrenSearchValue,
  } = params

  return React.useMemo(() => {
    const resolveAllAccountsCompany = () => allAccountsOption || null

    const buildSummary = (): SelectionSummary => {
      switch (pendingSelectionState.mode) {
        case 'allAccounts':
          return resolveAllAccountsCompany()
            ? { type: 'allAccounts', company: resolveAllAccountsCompany() as Company }
            : EMPTY_SELECTION_SUMMARY
        case 'multiple':
          return { type: 'multiple', companies: pendingSelectionState.items }
        case 'single':
          return pendingSelectionState.items[0]
            ? { type: 'single', company: pendingSelectionState.items[0] }
            : EMPTY_SELECTION_SUMMARY
        default:
          return EMPTY_SELECTION_SUMMARY
      }
    }

    const label = (() => {
      if (isOrphanSelected) {
        if (pendingSelectionState.mode === 'multiple') {
          return `Sélection (${pendingSelectionState.items.length}/${pendingSelectionLimit})`
        }
        const selected = pendingSelectionState.items[0]
        if (selected) {
          return entityDisplayName(selected)
        }
        return childrenSearchValue || 'Sélectionner un compte'
      }
      switch (pendingSelectionState.mode) {
        case 'allAccounts':
          return resolveAllAccountsCompany()
            ? entityDisplayName(resolveAllAccountsCompany() as Company)
            : ''
        case 'single':
          return pendingSelectionState.items[0]
            ? entityDisplayName(pendingSelectionState.items[0])
            : ''
        case 'multiple':
          return `Sélection (${pendingSelectionState.items.length}/${pendingSelectionLimit})`
        default:
          return isChildSelectorDisabled ? 'Sélectionner une famille' : 'Sélectionner un compte'
      }
    })()

    const displayValue = (() => {
      if (isOrphanSelected) {
        if (pendingSelectionState.mode !== 'none') {
          return label
        }
        return childrenSearchValue
      }
      return pendingSelectionState.mode !== 'none' ? label : ''
    })()

    const placeholder = (() => {
      if (isOrphanSelected) {
        return pendingSelectionState.mode !== 'none' || childrenSearchValue ? '' : label
      }
      return pendingSelectionState.mode !== 'none' ? '' : label
    })()

    const limitTooltip = pendingSelectionLimitReached
      ? `Limite de ${pendingSelectionLimit} comptes atteinte`
      : ''

    const displayedPendingValue = (() => {
      if (pendingSelectionState.mode === 'allAccounts' && allAccountsOption) {
        return [allAccountsOption]
      }
      return pendingSelectionState.items
    })()

    return {
      label,
      displayValue,
      placeholder,
      limitTooltip,
      summary: buildSummary(),
      displayedPendingValue,
    }
  }, [
    allAccountsOption,
    childrenSearchValue,
    isChildSelectorDisabled,
    isOrphanSelected,
    pendingSelectionLimit,
    pendingSelectionLimitReached,
    pendingSelectionState,
  ])
}

export { useChildSelectionView }
